import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, ShoppingBag, Check } from 'lucide-react';
import { locations } from '../data/mockData';
import { useCart } from '../context/CartContext';

/**
 * DressCatalog – Booqable embed (datepicker + product list) for DRESSES ONLY.
 *
 * - Hides accessory products visually (keyword tagging).
 * - Adds a custom "Añadir al carrito" button onto each visible dress card
 *   (consultation cart only; no online checkout).
 * - Booqable's script, pagination, datepicker and availability untouched.
 */
const ACCESSORY_KEYWORDS = [
  'tacón', 'tacon', 'tacones', 'zapato', 'zapatos', 'shoe', 'shoes', 'heel', 'heels',
  'arete', 'aretes', 'earring', 'earrings',
  'joyer', 'jewelry',
  'bolso', 'cartera', 'clutch', 'bag', 'handbag',
  'accesori',
  'bufanda', 'velo', 'collar', 'pulsera', 'anillo'
];

const DressCatalog = forwardRef((_, ref) => {
  const sectionRef = useRef(null);
  const { addItem, items } = useCart();
  const [portals, setPortals] = useState([]);

  useImperativeHandle(ref, () => ({
    filterByOccasion: () => {
      const el = document.getElementById('catalog');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }));

  // Init Booqable
  useEffect(() => {
    const tryInit = () => {
      if (window.Booqable && typeof window.Booqable.init === 'function') {
        try { window.Booqable.init(); } catch (e) { /* noop */ }
      }
    };
    tryInit();
    const t = setTimeout(tryInit, 600);
    return () => clearTimeout(t);
  }, []);

  // Hide accessory products visually
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const isAccessory = (text) => {
      const t = (text || '').toLowerCase();
      return ACCESSORY_KEYWORDS.some((kw) => t.includes(kw));
    };

    const tagProducts = () => {
      const list = section.querySelector('.booqable-product-list');
      if (!list) return;
      const cards = list.querySelectorAll('.booqable-product');
      cards.forEach((card) => {
        const txt = (card.textContent || '').slice(0, 300);
        if (txt.length < 3) return;
        if (isAccessory(txt)) {
          card.setAttribute('data-dress-hidden', 'true');
        } else {
          card.removeAttribute('data-dress-hidden');
        }
      });
    };

    tagProducts();
    const obs = new MutationObserver(() => tagProducts());
    obs.observe(section, { childList: true, subtree: true, characterData: true });
    const interval = setInterval(tagProducts, 1200);
    return () => {
      obs.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Inject "Añadir al carrito" buttons into each visible dress card via portals
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const refreshPortals = () => {
      const list = section.querySelector('.booqable-product-list');
      if (!list) { setPortals((prev) => (prev.length === 0 ? prev : [])); return; }
      const cards = Array.from(list.querySelectorAll('.booqable-product'));
      const next = [];
      cards.forEach((card) => {
        if (card.getAttribute('data-dress-hidden') === 'true') return;
        const name = extractName(card);
        if (!name) return;
        let host = card.querySelector(':scope > .morea-cart-host');
        if (!host) {
          host = document.createElement('div');
          host.className = 'morea-cart-host';
          card.appendChild(host);
        }
        next.push({ host, name, key: name });
      });
      // Only update state when the set of names actually changed
      setPortals((prev) => {
        if (prev.length === next.length && prev.every((p, i) => p.key === next[i].key)) {
          return prev;
        }
        return next;
      });
    };

    refreshPortals();
    const obs = new MutationObserver(refreshPortals);
    obs.observe(section, { childList: true, subtree: true });
    const interval = setInterval(refreshPortals, 1200);
    return () => {
      obs.disconnect();
      clearInterval(interval);
    };
  }, []);

  const handleAdd = useCallback(
    (name) => addItem({ name, type: 'vestido' }),
    [addItem]
  );

  const isInCart = (name) =>
    items.some((i) => i.name.toLowerCase() === name.toLowerCase());

  const buildGeneralWhatsApp = (city) => {
    const loc = locations.find((l) => l.city === city);
    if (!loc) return '#';
    const message = `Hola Morea, quiero consultar disponibilidad de un vestido que vi en la página para la sede de ${city}.`;
    const phone = String(loc.whatsapp).replace(/[^\d]/g, ''); // wa.me requires digits only
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="catalog" className="py-20 bg-white" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-serif text-neutral-900 mb-4">
            Catálogo
          </h2>
          <p className="text-neutral-600 font-light max-w-2xl mx-auto">
            Selecciona las fechas de tu evento y explora la disponibilidad real
            de nuestros vestidos. Añádelos al carrito de consulta y envíalos por
            WhatsApp en Quito o Guayaquil.
          </p>
        </div>

        <div className="booqable-embed-area catalog-list">
          <div className="max-w-3xl mx-auto mb-10">
            <p className="text-sm font-medium text-neutral-500 mb-3 tracking-wide uppercase text-center">
              Selecciona las fechas de tu evento
            </p>
            <div className="booqable-datepicker" data-testid="booqable-datepicker"></div>
          </div>

          <div className="booqable-product-list" data-testid="booqable-product-list"></div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-12">
          <a
            href={buildGeneralWhatsApp('Quito')}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catalog-whatsapp-quito"
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-6 py-3 rounded-sm transition-all duration-300 hover:shadow-lg w-full sm:w-auto text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Consultar por WhatsApp Quito
          </a>
          <a
            href={buildGeneralWhatsApp('Guayaquil')}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catalog-whatsapp-guayaquil"
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-6 py-3 rounded-sm transition-all duration-300 hover:shadow-lg w-full sm:w-auto text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Consultar por WhatsApp Guayaquil
          </a>
        </div>
      </div>

      {portals.map(({ host, name }) =>
        ReactDOM.createPortal(
          <AddToCartButton
            name={name}
            inCart={isInCart(name)}
            onAdd={() => handleAdd(name)}
          />,
          host
        )
      )}
    </section>
  );
});

function AddToCartButton({ name, inCart, onAdd }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inCart) onAdd();
      }}
      data-testid={`dress-add-${slug(name)}`}
      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm py-2 rounded-sm transition-all duration-300 border ${
        inCart
          ? 'bg-neutral-100 text-neutral-700 border-neutral-200 cursor-default'
          : 'bg-neutral-900 hover:bg-neutral-800 text-white border-neutral-900'
      }`}
    >
      {inCart ? (
        <>
          <Check className="w-3.5 h-3.5" />
          En carrito
        </>
      ) : (
        <>
          <ShoppingBag className="w-3.5 h-3.5" />
          Añadir al carrito
        </>
      )}
    </button>
  );
}

function extractName(card) {
  const nameEl = card.querySelector('h2, h3, h4, h5, [class*="title" i], [class*="name" i]');
  if (nameEl) {
    return (nameEl.textContent || '').trim().split('\n')[0].slice(0, 80);
  }
  const raw = (card.textContent || '').trim();
  const cleaned = raw.replace(/\$[\d.,]+/g, '').trim();
  return cleaned.split('\n')[0].slice(0, 80);
}

function slug(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
}

DressCatalog.displayName = 'DressCatalog';

export default DressCatalog;
